import type { SupabaseClient } from "@supabase/supabase-js";

import { SupabaseExecutionJobRepository } from "../../execution/supabaseJobRepository";

import { ExecutionJobService } from "../../execution/jobService";

import { SupabaseExecutionProgramRepository } from "./supabaseExecutionProgramRepository";

import { SupabaseExecutionLinkRepository } from "./supabaseExecutionLinkRepository";

import { ExecutionQueueDelegation } from "./executionQueueDelegation";

import { ExecutionJobQueueAdapter } from "./executionJobQueueAdapter";

import { AgentRegistry, type AgentDefinition } from "./agentRegistry";

import { AgentAssignmentService } from "./agentAssignmentService";

import { MultiAgentScheduler } from "./multiAgentScheduler";

import { createBrowserTakeoverBridge } from "./createBrowserTakeoverBridge";

import type { RepositorySnapshotReader } from "../intake/repositorySweepService";

import type { CapabilityAvailabilityReader } from "../autonomy/workerSelectionService";

import { createId } from "../../../app/runtimeContext";

export interface LiveTakeoverRuntimeDependencies {
  supabase: SupabaseClient;

  repositoryReader: RepositorySnapshotReader;

  capabilityAvailability: CapabilityAvailabilityReader;

  organizationId: string;

  productId?: string;

  projectId?: string;

  agents: readonly AgentDefinition[];
}

/**
 * Canonical live DI assembly for Cyan takeover.
 *
 * Cyan schedules governed work.
 * execution_jobs remains the persistent queue authority.
 * Workers remain the execution authority.
 */
export function createLiveTakeoverRuntime(
  dependencies: LiveTakeoverRuntimeDependencies,
) {
  const programs = new SupabaseExecutionProgramRepository(
    dependencies.supabase,
  );

  const executionJobs = new SupabaseExecutionJobRepository(
    dependencies.supabase,
  );

  const jobService = new ExecutionJobService(executionJobs);

  const queue = new ExecutionJobQueueAdapter(jobService, {
    organizationId: dependencies.organizationId,

    productId: dependencies.productId,

    projectId: dependencies.projectId,
  });

  const delegation = new ExecutionQueueDelegation(queue, {
    createExecutionId: () => createId("cyan_execution"),
  });

  const registry = new AgentRegistry();

  for (const agent of dependencies.agents) {
    registry.register(agent);
  }

  const assignment = new AgentAssignmentService(registry);

  const scheduler = new MultiAgentScheduler(programs, assignment, delegation);

  const takeoverBridge = createBrowserTakeoverBridge({
    supabase: dependencies.supabase,

    repositoryReader: dependencies.repositoryReader,

    capabilityAvailability: dependencies.capabilityAvailability,

    scheduler,
  });

  return {
    takeoverBridge,
    scheduler,
    programs,
    executionJobs,
    jobService,
    executionLinks: new SupabaseExecutionLinkRepository(dependencies.supabase),
  };
}
